/**
 * @file 回帖编辑器
 * @author Yangholmes 2023-12-20
 */

import { useState, useRef, useMemo, useContext, forwardRef, useImperativeHandle } from 'react';
import { useTranslations } from 'next-intl';
import cn from 'classnames';
import UploadModal, { IOnChangeCbParam } from '../UploadModal';
import { ActionTypeEnum } from '../ModuleEditor/config';
import { IMAGE_ACCEPT_FORMAT } from '@/constants/upload';
import CommonTextarea, { CommonTextareaExpose } from '../CommonTextarea';
import PhotoPreview from '../PhotoPreview';
import { uploadPhotoService } from '@/api/upload';
import { ErrorCodes } from '@/utils/fetch';
import Styles from './editor.module.scss'
import { EmojiPopup } from './EmojiPopup';
import { StoreContext } from '@/context';
import Toast from '../Toast';

/** 附件功能配置 */
const attachmentConfig = [{
  type: ActionTypeEnum.photo,
  labelKey: 'btn_photo',
  isUploadFile: true,
  multiple: true,
  accept: IMAGE_ACCEPT_FORMAT,
}]

/** 图片附件类型 */
type ImageType = {
  /** 选中图片的文件 */
  file: File;
  /** 图片预览资源路径 */
  preUrl: string;
  /** oss 资源路径 */
  url: string | null;
  /** 图片上传进度 */
  percent: number;
  /** 上传错误 */
  error?: Error;
  isCompleted?: boolean;
}

/** 编辑器 props */
export interface EditorProps {
  /** 空显示内容 */
  placeholder?: string;
  /** 初始化数据 */
  initValue?: {
    /** 未转译字符串 */
    textRaw: string;
    /** 图片 */
    image: ImageType;
  };
  onChange?: (value: {
    /** 未转译字符串 */
    textRaw: string;
    /** 图片 */
    image: ImageType | null;
  }) => void;
  /** 发布点击回调 */
  onPublish?: (content: string) => Promise<void>;
  /** 类名 */
  className?: string;
  autoFocused?: boolean;
}

export interface EditorExpose {
  /** 重置编辑器 */
  reset: (gear?: 0 | 1) => void;
  onFocus: () => void;
}

export const Editor = forwardRef<EditorExpose, EditorProps>(function Editor(props, ref) {
  const {
    placeholder = '',
    initValue,
    autoFocused,
    onChange,
    onPublish,
    className
  } = props
  const t = useTranslations();
  const { state } = useContext(StoreContext);
  const { emojies = [] } = state['detail']

  /** 为空时显示的内容 */
  const _placeholder = useMemo(() => placeholder || t('thread_comment_placeholder'), [placeholder])

  const textaresRef = useRef<CommonTextareaExpose>(null)

  /** 是否显示图片上传弹窗 */
  const [uploadModalVisible, setUploadModalVisible] = useState(false)
  const [uploadType, setUploadType] = useState<ActionTypeEnum>(ActionTypeEnum.photo)

  /** 是否显示表情包 */
  const [isEmojiPopupShow, setIsEmojiPopupShow] = useState(false)

  /** 内容是否正在发布中 */
  const [isPublishing, setIsPublishing] = useState(false);

  /** 用户填入的内容 */
  const [contentText, setContentText] = useState(initValue?.textRaw || '')
  /** 光标选中的文本位置 */
  const [currentPosition, setCurrentPosition] = useState<[number, number]>([0, 0])
  const [image, setIamge] = useState<ImageType | null>(initValue?.image || null)

  /** 是否聚焦到输入框 */
  const [isFocusonText, setIsFocusonText] = useState(false)
  const isNull = useMemo(() => {
    return contentText === '' && image === null && !isFocusonText
  }, [contentText, image, isFocusonText])

  /**
   * 能否发布
   *
   * 1. 没有图片在上传中
   * 2. 上一个发布完成
   */
  const canPublish = useMemo(() => {
    if (image) {
      return ((image.percent === 1 && image?.isCompleted) || image.percent === 0) && !isPublishing
    }
    return !isPublishing
  }, [image, isPublishing])

  const onContentTextChange = (_: string, text?: string, p: [number, number] = [0, 0]) => {
    setContentText(text || '')
    setCurrentPosition(p)
    onChange?.({ textRaw: text || '', image: image })
  }

  const onPopupEmoji = () => {
    setIsEmojiPopupShow(true)
  }

  const onInsertEmoji = (emoji: string) => {
    if (isPublishing) {
      return
    }
    textaresRef.current?.insertText(emoji, currentPosition)
  }

  const onUpload = (type: ActionTypeEnum) => {
    if (isPublishing) {
      return
    }
    setUploadType(type)
    setUploadModalVisible(true)
  }

  const uploadImage = async (file: File, preUrl: string) => {
    const controller = new AbortController();
    const _image = await uploadPhotoService({
      file,
      controller,
      onUploadProgressCb(percent) {
        setIamge({
          file,
          preUrl,
          url: null,
          percent,
          isCompleted: false,
        });
      },
    }).then((url: string) => {
      return {
        file,
        preUrl: url,
        url,
        percent: 1,
        isCompleted: true,
      };
    }).catch((e) => {
      if(e?.code === ErrorCodes.requestLimit) {
        Toast.show({
          content: e?.message || t('tips_request_max_limit'),
        });
      }
      return {
        file,
        preUrl,
        url: null,
        percent: 0,
        isCompleted: false,
        error: e
      };
    })
    setIamge(_image);
    onChange?.({ textRaw: contentText, image: image })
  }

  const onUploadImageChange = ({ acceptFiles }: IOnChangeCbParam) => {
    const { file, width, height, duration, url } = acceptFiles[0];
    setIamge({
      file,
      preUrl: url,
      url: null,
      percent: 0,
      isCompleted: false,
    })
    setUploadModalVisible(false);
    uploadImage(file, url);
  }

  const onUploadImageRetry = () => {
    if (!image) {
      return
    }
    const {file, preUrl} = image
    setIamge({
      file,
      preUrl,
      url: null,
      percent: 0,
      isCompleted: false,
    })
    uploadImage(file, preUrl);
  }

  const _onPublish = () => {
    if (!canPublish) {
      return;
    }
    let imageContent = '';
    if (image?.percent === 1 && image?.url) {
      imageContent = `<img src="${image.url}" />`
    }
    const contentText = textaresRef.current?.getEscapedText().trim()
    const content = `${contentText}${imageContent}`;
    if (!content) {
      Toast.show({
        content: '内容不能为空哦'
      })
      return;
    }
    setIsPublishing(true);
    onPublish?.(content).then(() => {
      setIamge(null);
      setContentText('');
      setIsFocusonText(false);
    }).finally(() => {
      setIsPublishing(false);
    });
  }

  /**
   * 重置编辑器，可以选择浅重置、深重置
   * 0：浅重置
   * 1：深重置
   * @param gear 档位 0 | 1
   */
  const reset = (gear?: 0 | 1) => {
    const act = {
      0: setIsFocusonText(false),
      1: () => {
        setContentText('');
        setIamge(null);
        setIsFocusonText(false);
      }
    }
    act[gear || 0]?.()
  }

  useImperativeHandle(ref, () => ({
    reset,
    onFocus: () => textaresRef.current?.handleFocus(),
  }))

  return (
    <div className={cn(
      Styles['pcRoot'],
      className,
      isNull ? Styles.contentNull : ''
    )}>
      <div className={cn(Styles.contentText)}>
        <CommonTextarea
          id="reply-editor"
          ref={textaresRef}
          rows={isNull ? 1 : 5}
          placeholder={_placeholder}
          value={contentText}
          focused={autoFocused}
          onChange={onContentTextChange}
          onFocus={() => setIsFocusonText(true)}
        />
      </div>
      {
        /** 图片预览区 */
        <div className={cn(Styles.imagePreviewArea)}>
          {image ? <div
            style={{
              display: 'flex',
              paddingBottom: '12px',
            }}
            >
              <PhotoPreview
                id="reply-editor"
                isEdit
                url={image?.preUrl}
                percent={image?.percent || 0}
                completed={image?.isCompleted}
                error={image?.error}
                onRemove={() => {setIamge(null)}}
                onRetry={onUploadImageRetry}
                style={{
                  width: 'auto',
                  height: 'auto',
                  maxHeight: '187px',
                  maxWidth: '199px',
                }}
                className={Styles.photoWrapper}
              />
            </div> : null}
        </div>
      }
      <div className={cn(Styles.functions)}>
        <div className={cn(Styles.insert)}>
          {attachmentConfig.map(item => (<div key={item.type}>
            <div className={cn('tooltip', 'tooltipTop')} data-tooltip={t(item.labelKey)} onClick={() => onUpload(item.type)}>
              <div className={cn(Styles.icon)} style={{
                backgroundImage: `url(${require(`@/assets/icon/${item.type}.png`).default.src})`
              }} />
            </div>
          </div>))}
          <div className={cn('tooltip', 'tooltipTop')} data-tooltip={t('tips_thread_emoji')} onClick={onPopupEmoji}>
            <div className={cn(Styles.icon)} style={{
              backgroundImage: `url(${require('@/assets/icon/emoji.png').default.src})`
            }} />
          </div>
          {
            /** 表情包气泡 */
            isEmojiPopupShow ? <EmojiPopup className={cn(Styles.emoji)} emojies={emojies.filter(e => e.shownOnReply)} onSelect={(code: string) => onInsertEmoji(code)} onClose={() => setIsEmojiPopupShow(false)} /> : null
          }
        </div>
        {
          /** 发布按钮 */
          <button
            className={cn(Styles.publishBtn)}
            onClick={_onPublish}
            disabled={!canPublish}
          >
            {isPublishing ? t('btn_publishing') : t('btn_publish')}
          </button>
        }
      </div>
      {uploadModalVisible ? <UploadModal
        uploadType={uploadType}
        title={t('thread_add_photo')}
        multiple={false}
        onCancel={() => setUploadModalVisible(false)}
        onChange={onUploadImageChange}
      /> : null}
    </div>
  )
})

